import React, {useEffect, useState, useRef} from "react";
import GenericLayout from "layouts/generic";
import css from "./index.module.scss";
import ContactSection from "sections/contact";
import { useInView } from "react-intersection-observer";
import { gsap } from "scripts/gsap";
import SplitText from "@/components/splitText";
import slideFadeIn from "scripts/animations/slideFadeIn";
import imageSlideFadeIn from "scripts/animations/imageSlideFadeIn";
import inViewSettings from "scripts/animations/inViewSettings";
import TacticsSmall, {TACTICS} from "@/sections/tacticsSmall";
import strapi from "scripts/strapi";
import TestimonialsSpotlight, {TESTIMONIALS} from "@/sections/testimonialsSpotlight";
import Fold1Section from "@/sections/fold1";
import BasicCardSection from "@/sections/basicCards";
import FlowChartSection from "@/sections/flowChart";
import MagnifyingGlassChartSvg from "/public/images/icons/magnifyingGlassChart.svg";
import ArrowsToEyeSvg from "/public/images/icons/arrowsToEye.svg";
import ChessSvg from "/public/images/icons/chess.svg";
import ArrowPointerSvg from "/public/images/icons/arrowPointer.svg";
import Articles from "@/sections/articles";
import GoogleAdsSvg from "/public/images/logos/googleAds/googleAds.svg";

export default function LandingPageDevelopment({articles}) {
  const [elements, setElements] = useState({
    fold: {
      infographic: useInView(inViewSettings((targets) => {
        let chart = targets[0];
        let tl = gsap.timeline({});

        let entries = chart.querySelectorAll(`.${css.entry}`);
        let explanation = chart.querySelectorAll(`.${css.explanation}`);

        tl.set(chart, {visibility: "visible", opacity: 1});
        for (let entry of entries){
          let innerTl = gsap.timeline();
          innerTl.fromTo(entry.querySelector(`.${css.label}`), {
            opacity: 0,
          }, {
            opacity: 1,
            duration: 0.25,
            ease: "sine.inOut",
          });
          innerTl.fromTo(entry.querySelector(`.${css.bar}`), {
            scaleY: 0,
            transformOrigin: "bottom center",
          }, {
            scaleY: 1,
            duration: 0.5,
            ease: "sine.inOut",
          }, "<10%");
          innerTl.fromTo(entry.querySelector(`.${css.percent}`), {
            opacity: 0,
          }, {
            opacity: 1,
            duration: 0.25,
            ease: "sine.inOut",
          }, "<80%");
          tl.add(innerTl, "<25%");
        }
        tl.fromTo(explanation, {
          opacity: 0,
        }, {
          opacity: 1,
          duration: 0.25,
          ease: "sine.inOut",
        }, "<50%");
      })),
    },
    explanation: {
      text: useInView(inViewSettings(slideFadeIn)),
      headline: useInView(inViewSettings(slideFadeIn)),
      image: useInView(inViewSettings(imageSlideFadeIn)),
    },
  });

  return <GenericLayout
    title="Unlock the Full Potential of Google Ads | Deviate Labs"
    description="Our expert ad team develops breakthrough creatives tailored to your brand's unique identity and goals."
    color="purple"
  >
    <Fold1Section
      headline={<>
        <div>Unlock the Full Potential</div>
        <div>of Google Ads</div>
      </>}
      className={css.fold}
      subheadline="Our comprehensive audit and optimization service takes your campaigns to the next level."
      image={
        <div className={`target ${css.table}`} ref={elements.fold.infographic.ref}>
          <div className={css.entries}>
            <div className={css.entry}>
              <div className={css.percent}>2.7%</div>
              <div className={css.bar}></div>
              <div className={css.label}>Ecomm</div>
            </div>
            <div className={css.entry}>
              <div className={css.percent}>2.4%</div>
              <div className={css.bar}></div>
              <div className={css.label}>B2B</div>
            </div>
            <div className={css.entry}>
              <div className={css.percent}>3.2%</div>
              <div className={css.bar}></div>
              <div className={css.label}>Health</div>
            </div>
            <div className={css.entry}>
              <div className={css.percent}>2.1%</div>
              <div className={css.bar}></div>
              <div className={css.label}>Tech</div>
            </div>
            <div className={css.entry}>
              <div className={css.percent}>3.7%</div>
              <div className={css.bar}></div>
              <div className={css.label}>Home</div>
            </div>
          </div>
          <div className={css.explanation}>Average ad click through rate.</div>
        </div>
      }
    />


    <FlowChartSection
      headline="Our Proven Process"
      subheadline="Our four-step process for taking your Google Ads performance to the next level. "
      cards={[
        {
          headline: "Step 1: Full Campaign Audit",
          body: "We thoroughly audit your existing Google Ads account, evaluating structure, settings, targeting, bidding, cost-per-action, and more.",
          icon: <MagnifyingGlassChartSvg/>,
        },
        {
          headline: "Step 2: Insight Generation",
          body: "Our experts identify opportunities related to campaign setup, ad relevance, quality score, audience targeting, keyword optimization, and conversion optimization.",
          icon: <ArrowsToEyeSvg/>,
        },
        {
          headline: "Step 3: Strategic Planning",
          body: "We provide a strategic roadmap to boost campaign performance, lower cost-per-click, improve conversion rates, and increase ROI.",
          icon: <ChessSvg/>,
        },
        {
          headline: "Step 4: Execution",
          body: "Our team makes recommended changes to the account and provides ongoing optimization to ensure continued success.",
          icon: <ArrowPointerSvg/>,
        },
      ]}
    />


    <BasicCardSection
      headline="Benefits of a Google Ads Audit"
      subheadline="A professional audit provides tremendous value by aligning your campaigns with best practices."
      width="2"
      color="cyan"
      cards={[
        {
          name: "Increased Efficiency",
          body: <>We meticulously analyze campaign performance to identify and eliminate wasted spend. By removing ineffective keywords, low-performing ads, and poorly converting placements, we refocus your budget on the highest ROI opportunities.</>,
        },
        {
          name: "Improved Performance",
          body: <>Auditing carefully examines the critical factors impacting account performance like quality score, ad relevance, and bid strategies. We optimize these key areas through A/B testing, landing page improvements, and granular bid adjustments to boost results.</>,
        },
        {
          name: "Higher ROI",
          body: <>Beyond campaign structure, we dive deep into audience targeting and maximize conversion rates through landing page optimization and funnel analysis. Our strategies aim to reduce cost-per-conversion and increase return on ad spend.</>,
        },
        {
          name: "Increased Visibility",
          body: <>Reconfiguring targeting parameters allows us to reach more of your ideal customers. Expanding placement and device targeting gives your ads the exposure needed to connect with high-intent users.</>,
        },
      ]}
    />


    <section className={`section ${css.explanation}`}>
      <div className="sectionInner">
        <h2 ref={elements.explanation.headline.ref}>
          <SplitText className="target" text="Google Ads Management"/>
        </h2>
        <div className={css.row}>
          <div ref={elements.explanation.image.ref}>
            <GoogleAdsSvg className="target"/>
          </div>
          <div className={css.text} ref={elements.explanation.text.ref}>
            <p>
              <SplitText className="target" text="We also offer a comprehensive Google Ads management service that provides continuous optimization after the initial audit. We become an extension of your marketing team, taking over the day-to-day administration while you focus on higher-level strategy."/>
            </p>
            <p>
              <SplitText className="target" text="Our experts monitor your accounts daily, adjusting bids, budgets, and targets in response to performance. We create new ad variations, expand your keywords, and test innovative strategies to keep increasing results. You get a dedicated account manager along with the full capabilities of our agency."/>
            </p>
            <p>
              <SplitText className="target" text="We handle time-consuming tasks like creating new ad copy, designing display ads, managing budgets across campaigns, optimizing for different goals, reporting, and more. You gain insights from regular performance analysis and strategy calls."/>
            </p>
            <p>
              <SplitText className="target" text="With ongoing management from our Google Ads specialists, you can count on up-to-date strategies, improved ROI, and maximum return from your campaigns month after month. We make it easy to scale up your success."/>
            </p>
          </div>
        </div>
      </div>
    </section>



    <TestimonialsSpotlight testimonial={TESTIMONIALS.TIVVIT}/>


    <TacticsSmall
      headline="Other Growth Marketing Services"
      subheadline="No matter your company, we have the expertise to optimize your growth."
      enabled={[
        TACTICS.HIGH_PERFORMANCE_LANDING_PAGES,
        TACTICS.CONTENT_MARKETING,
        TACTICS.CONVERSION_RATE_OPTIMIZATION,
        TACTICS.INFLUENCER_MARKETING,
        TACTICS.HIGH_PERFORMANCE_AD_CREATIVES,
      ]}
    />

    <Articles
      articles={articles}
      headline="Learn More About Ad Optimization"
      subheadline="We've written extensively about Google Ads and other growth marketing topics."
    />

    <ContactSection/>

  </GenericLayout>;
}


export async function getStaticProps() {
  let data = await strapi.article.findMany({
    filters: {
      id: {
        $eq: [33, 32, 27],
      },
    },
    populate: {
      coverArt: "*",
    },
    fields: [ //don't include the body since it's large and unnecessary
      "headline",
      "slug",
      "teaser",
      "priority",
      "category",
      "publishedAt",
    ],
  });

  return {
    props: {
      articles: data,
    },
    revalidate: 60,
  };
}
